import { useEffect, useRef, useState } from "react";
import {
  EARLIEST_GAME_DAY,
  formatLocalGameDate,
  gameDateForDay,
  latestGameDay,
} from "@workspace/shared";
import { useIsOnline } from "../../../hooks/index.ts";
import type { LibraryGame } from "./adapter.ts";

/** A picked day saves after this pause even if the picker keeps focus. */
export const GAME_DATE_SAVE_PAUSE_MS = 1200;

type Props = {
  game: LibraryGame;
  /**
   * Saves the game's new date (an ISO timestamp); resolves true once the
   * server has it. Omit to keep the date read-only.
   */
  onSave?: (gameId: number, gameDate: string) => Promise<boolean>;
};

/**
 * A game's date in the Game Library — ported from the in-game app
 * (platform-2.0 features/folder/GameDateCell, client 2026-10-06): tapping a
 * saved game's date opens the date picker — the native one on the iPad —
 * drawn invisibly over the text, so the date keeps its look.
 *
 * The pick saves when the picker closes, or after a short pause (a desktop
 * calendar keeps focus), or when the row goes away; never half-typed. It is
 * the cell's own state, so it can't outlive the field: going offline drops an
 * unsaved pick instead of leaving it on screen as if saved. The current
 * (in-progress) game's date is set when it is saved, so it stays plain text.
 */
export function GameDateCell({ game, onSave }: Props) {
  const { isOnline } = useIsOnline();
  const gameId = game.game_id;
  const savedDay = formatLocalGameDate(game.game_date);
  // What the server has once a save lands, until the list catches up — a
  // list reload that fails must not put the old date back on screen.
  const [confirmed, setConfirmed] = useState<string | null>(null);
  if (confirmed != null && confirmed === savedDay) setConfirmed(null);
  const [draft, setDraftState] = useState<string | null>(null);
  const editable =
    Boolean(onSave) && Boolean(game.complete) && gameId != null && isOnline;

  // Read by the handlers, which can run in the same event as the change or
  // Escape that moved them (before any re-render).
  const live = useRef({ draft, baseline: confirmed ?? savedDay, onSave });
  live.current.baseline = confirmed ?? savedDay;
  live.current.onSave = onSave;
  const saving = useRef(false);
  const focused = useRef(false);
  const pause = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const setDraft = (day: string | null) => {
    live.current.draft = day;
    setDraftState(day);
  };

  const save = async () => {
    clearTimeout(pause.current);
    const day = live.current.draft;
    const persist = live.current.onSave;
    if (day == null || gameId == null || !persist || saving.current) return;
    const iso = day !== live.current.baseline ? gameDateForDay(day) : null;
    if (!iso) {
      setDraft(null);
      return;
    }
    saving.current = true;
    let saved = false;
    try {
      saved = await persist(gameId, iso);
    } catch {
      // The page says what went wrong.
    } finally {
      saving.current = false;
    }
    if (saved) {
      live.current.baseline = day;
      setConfirmed(day);
    }
    // Picked again while that was saving: save the newer pick next (after
    // the usual pause while the picker is still open).
    const newer = live.current.draft;
    if (newer === day) setDraft(null);
    else if (newer != null && gameDateForDay(newer))
      pause.current = setTimeout(
        () => void save(),
        focused.current ? GAME_DATE_SAVE_PAUSE_MS : 0,
      );
  };

  // Offline the field goes away, and an unsaved pick with it.
  useEffect(() => {
    if (!editable) {
      clearTimeout(pause.current);
      if (live.current.draft != null) setDraft(null);
    }
  }, [editable]);

  // The row goes away (another page, a reload of the list) with a pick
  // still on it: save it, rather than lose what the coach already saw.
  const saveRef = useRef(save);
  saveRef.current = save;
  useEffect(
    () => () => {
      clearTimeout(pause.current);
      const day = live.current.draft;
      if (day != null && day !== live.current.baseline && !saving.current)
        void saveRef.current();
    },
    [],
  );

  const shown = formatLocalGameDate(draft ?? confirmed ?? game.game_date);
  if (!editable) return <>{formatLocalGameDate(confirmed ?? game.game_date)}</>;
  return (
    <>
      <input
        type="date"
        aria-label={`Date of ${game.game_description || "game"}`}
        className="peer absolute inset-0 h-full w-full cursor-pointer opacity-0"
        min={EARLIEST_GAME_DAY}
        max={latestGameDay()}
        value={draft ?? confirmed ?? savedDay}
        onClick={(e) => {
          e.stopPropagation();
          // Desktop browsers open the calendar only from its icon.
          try {
            e.currentTarget.showPicker?.();
          } catch {
            // Not allowed here: the field still takes typing.
          }
        }}
        onFocus={() => {
          focused.current = true;
        }}
        onChange={(e) => {
          setDraft(e.target.value);
          clearTimeout(pause.current);
          // Only a whole, valid day: typing a year passes through 0002…
          if (gameDateForDay(e.target.value))
            pause.current = setTimeout(() => void save(), GAME_DATE_SAVE_PAUSE_MS);
        }}
        onBlur={() => {
          focused.current = false;
          void save();
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
          if (e.key === "Escape") {
            clearTimeout(pause.current);
            setDraft(null);
            e.currentTarget.blur();
          }
        }}
      />
      <span
        aria-hidden
        className="rounded-sm underline decoration-gray-300 decoration-dotted underline-offset-4 peer-hover:text-blue-600 peer-focus-visible:ring-2 peer-focus-visible:ring-blue-500"
      >
        {shown}
      </span>
    </>
  );
}
